import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import jwt_decode from 'jwt-decode';
import Credentials from '../api/models/credentials';
import JWTToken from '../api/models/jwt';
import Tokens from '../api/models/tokens';
import storage, { StorageTokens } from '../common/localStorage';
import { impersonateUser, unimpersonateUser, postAuthenticate, postRevokeToken } from '../api/services/authService';
import Discipline from '../api/models/discipline';

type StatusType = 'idle' | 'logging_in' | 'authenticated' | 'failed';

export type UserState = {
  status: StatusType;
  statusMessage?: string;
  id?: number;
  loggedInUserId?: number;
  firstName?: string;
  middleInitial?: string;
  lastName?: string;
  email?: string;
  roles?: string[] | string;
  loggingOut?: boolean;
  hasLogoutError?: boolean;
  passwordAboutToExpire: Boolean;
  currentDiscipline?: Discipline;
  currentDisciplineId?: number;
  unreadMessagesCount?: number;
  sessionTime?: number;
  countDownTime?: number
};

type Profile = {
  firstName?: string;
  middleInitial?: string;
  lastName?: string;
  email?: string;
  jwt: string;
};

// Initialize state from storage
const BuildInitialState = (): UserState => {
  const localTokens = storage.GetTokens();

  const userState: UserState = {
    status: 'idle',
    passwordAboutToExpire: false
  };

  // If tokens are present, lets initialize them here.
  if (localTokens?.token && localTokens?.refreshToken) {
    const jwt = jwt_decode(localTokens.token) as JWTToken;
    userState.status = 'authenticated';
    userState.id = parseInt(jwt.sub);
    userState.loggedInUserId = parseInt(jwt.logged_in_user_id);
    userState.firstName = jwt.given_name;
    userState.lastName = jwt.family_name;
    userState.email = jwt.email;
    userState.roles = jwt.roles;
    userState.unreadMessagesCount = jwt.unread_messages_count;
    userState.currentDiscipline = undefined;
    userState.currentDisciplineId = -1;
  }

  return userState;
};

const initialState = BuildInitialState();

export const login = createAsyncThunk(
  'user/login',
  async ({ emailAddress, password, captchaToken }: Credentials) => {
    const resp = await postAuthenticate({
      emailAddress,
      password,
      captchaToken,
    });

    return resp.data;
  }
);

export const logout = createAsyncThunk('user/logout', async () => {
  const localTokens = storage.GetTokens();

  // If refreshToken doesn't exist, we can move on...
  if (!localTokens?.refreshToken) {
    return;
  }

  await postRevokeToken(localTokens.refreshToken);
  return;
});

export const impersonate = createAsyncThunk(
  'user/impersonate',
  async (userId: number) => {
    const response = await impersonateUser(userId);
    return response.data;
  }
)

export const unimpersonate = createAsyncThunk(
  'user/unimpersonate',
  async () => {
    const response = await unimpersonateUser();
    return response.data;
  }
)

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    clearLogoutError: (state) => {
      state.hasLogoutError = false;
    },
    reset: (state) => {
      state.status = 'idle';
      state.firstName = undefined;
      state.middleInitial = undefined;
      state.lastName = undefined;
      state.email = undefined;
      state.roles = undefined;
      state.currentDiscipline = undefined;
      state.currentDisciplineId = undefined;
      state.unreadMessagesCount = undefined;
      state.sessionTime = undefined;
      state.countDownTime = undefined;

      // Clear tokens from storage
      storage.ClearTokens();
    },
    syncTokens: (state, action: PayloadAction<StorageTokens>) => {
      storage.SetTokens(action.payload);

 

      // If tokens are present, lets initialize them here.
      if (action.payload.token && action.payload.refreshToken) {
        const jwt = jwt_decode(action.payload.token) as JWTToken;
        state.status = 'authenticated';
        state.firstName = jwt.given_name;
        state.lastName = jwt.family_name;
        state.email = jwt.email;
        state.roles = jwt.roles;
        state.unreadMessagesCount = jwt.unread_messages_count;
      }
    },
    updateProfile: (state, action: PayloadAction<Profile>) => {
      state.firstName = action.payload.firstName;
      state.middleInitial = action.payload.middleInitial;
      state.lastName = action.payload.lastName;
      state.email = action.payload.email;

      storage.SetAccessToken(action.payload.jwt);
    },
    updateUnreadMessagesCount : (state, action: PayloadAction<number>) => {
      state.unreadMessagesCount = action.payload > 0 ? action.payload : 0;
    },
    updateDiscipline: (state, action: PayloadAction<Discipline>) => {
      state.currentDiscipline = action.payload;
      state.currentDisciplineId = action.payload.disciplineId!;
      localStorage.setItem('discipline', JSON.stringify(state.currentDiscipline));
      localStorage.setItem('disciplineId', action.payload.disciplineId?.toString() ?? '-1');
    },
    updateDisciplineId: (state, action: PayloadAction<{ disciplineId: number }>) => {
      state.currentDisciplineId = action.payload.disciplineId;
      localStorage.setItem('disciplineId', action.payload.disciplineId?.toString());
    },
  },
  extraReducers: (builder) => {
    // Logout State
    builder.addCase(logout.fulfilled, (state) => {
      state.loggingOut = false;
      state.hasLogoutError = false;
      state.status = 'idle';
      state.firstName = undefined;
      state.middleInitial = undefined;
      state.lastName = undefined;
      state.email = undefined;
      state.roles = undefined;
      state.currentDisciplineId = -1;
      state.currentDiscipline = undefined;
      state.unreadMessagesCount = undefined;
      state.sessionTime = undefined;
      state.countDownTime = undefined;

      // Clear tokens from storage
      storage.ClearTokens();
    });
    builder.addCase(logout.pending, (state) => {
      state.loggingOut = true;
    });
    builder.addCase(logout.rejected, (state, action) => {
      state.loggingOut = false;
      state.hasLogoutError = true;
    });

    // Login State
    builder.addCase(login.fulfilled, (state, action: PayloadAction<Tokens>) => {
      // Store locally
      storage.SetTokens({
        token: action.payload.token,
        refreshToken: action.payload.refreshToken,
        sessionTime: action.payload.sessionTime.toString(),
        countDownTime: action.payload.countDownTime.toString()
      });


      // Update state
      var decodedJwt = jwt_decode(action.payload.token) as JWTToken;
      state.status = 'authenticated';
      state.id = Number(decodedJwt.sub);
      state.loggedInUserId = Number(decodedJwt.logged_in_user_id);
      state.firstName = decodedJwt.given_name;
      state.lastName = decodedJwt.family_name;
      state.email = decodedJwt.email;
      state.roles = decodedJwt.roles;
      state.passwordAboutToExpire = action.payload.passwordAboutToExpire;
      state.unreadMessagesCount = decodedJwt.unread_messages_count;

      state.sessionTime = action.payload.sessionTime;
      state.countDownTime = action.payload.countDownTime;

    });

    builder.addCase(login.pending, (state) => {
      state.status = 'logging_in';
      state.firstName = undefined;
      state.middleInitial = undefined;
      state.lastName = undefined;
      state.email = undefined;
      state.roles = undefined;
      state.unreadMessagesCount = undefined;
    });

    builder.addCase(login.rejected, (state, action) => {
      state.status = 'failed';
      state.statusMessage = action.error.message;
      state.firstName = undefined;
      state.middleInitial = undefined;
      state.lastName = undefined;
      state.email = undefined;
      state.roles = undefined;
      state.unreadMessagesCount = undefined;

      storage.ClearTokens();
    });

    builder.addCase(impersonate.fulfilled, (state, action: PayloadAction<Tokens>) => {
      // Store locally
      storage.SetTokens({
        token: action.payload.token,
        refreshToken: action.payload.refreshToken,
        sessionTime: action.payload.sessionTime.toString(),
        countDownTime: action.payload.countDownTime.toString()
      });

      // Update state
      var decodedJwt = jwt_decode(action.payload.token) as JWTToken;
      state.status = 'authenticated';
      state.id = Number(decodedJwt.sub);
      state.loggedInUserId = Number(decodedJwt.logged_in_user_id);
      state.firstName = decodedJwt.given_name;
      state.lastName = decodedJwt.family_name;
      state.email = decodedJwt.email;
      state.roles = decodedJwt.roles;
      state.unreadMessagesCount = decodedJwt.unread_messages_count;
      state.passwordAboutToExpire = action.payload.passwordAboutToExpire;

      state.sessionTime = action.payload.sessionTime;
      state.countDownTime = action.payload.countDownTime;
    });

    builder.addCase(impersonate.pending, (state) => {
      state.status = 'logging_in';
      state.firstName = undefined;
      state.middleInitial = undefined;
      state.lastName = undefined;
      state.email = undefined;
      state.roles = undefined;
      state.unreadMessagesCount = undefined;
    });

    builder.addCase(impersonate.rejected, (state, action) => {
      state.statusMessage = action.error.message;
      state.status = 'failed';
      state.firstName = undefined;
      state.middleInitial = undefined;
      state.lastName = undefined;
      state.email = undefined;
      state.roles = undefined;
      state.unreadMessagesCount = undefined;

      storage.ClearTokens();
    });

    builder.addCase(unimpersonate.fulfilled, (state, action: PayloadAction<Tokens>) => {
      // Store locally
      storage.SetTokens({
        token: action.payload.token,
        refreshToken: action.payload.refreshToken,
        sessionTime: action.payload.sessionTime.toString(),
        countDownTime: action.payload.countDownTime.toString()
      });


      // Update state
      var decodedJwt = jwt_decode(action.payload.token) as JWTToken;
      state.status = 'authenticated';
      state.id = Number(decodedJwt.sub);
      state.loggedInUserId = Number(decodedJwt.logged_in_user_id);
      state.firstName = decodedJwt.given_name;
      state.lastName = decodedJwt.family_name;
      state.email = decodedJwt.email;
      state.roles = decodedJwt.roles;
      state.passwordAboutToExpire = action.payload.passwordAboutToExpire;

      // Store the sessionTime and countDownTime from the API response
      state.sessionTime = action.payload.sessionTime;
      state.countDownTime = action.payload.countDownTime;
    });

    builder.addCase(unimpersonate.pending, (state) => {
      state.status = 'logging_in';
      state.firstName = undefined;
      state.middleInitial = undefined;
      state.lastName = undefined;
      state.email = undefined;
      state.roles = undefined;
      state.unreadMessagesCount = undefined;
    });

    builder.addCase(unimpersonate.rejected, (state) => {
      state.status = 'failed';
      state.firstName = undefined;
      state.middleInitial = undefined;
      state.lastName = undefined;
      state.email = undefined;
      state.roles = undefined;
      state.unreadMessagesCount = undefined;

      storage.ClearTokens();
    });
  },
});

export const { reset, syncTokens, clearLogoutError, updateProfile, updateDiscipline, updateDisciplineId, updateUnreadMessagesCount } =
  userSlice.actions;
export default userSlice.reducer;
